import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import {
  Alert,
  Button,
  Input,
  Loader,
  Modal,
  Select,
  Table,
  Toggle
} from '@itds/elements/index.js';
import { formatInTimeZone } from 'date-fns-tz';
import { STATES } from '@itds/Constants';
import { Colors, FontSizes } from '@itds/Theme';

import VersionForm from './forms/VersionForm';
import {
  fetchForms,
  fetchFormMetadata,
  toggleFormStatus,
  deleteForm,
  setSearchQuery,
  setCurrentPage,
  exportFormsToCSV,
  setFilters,
  clearCache,
  clearMessages,
  setSuccessMessage,
  setCurrentCarrier
} from '@redux/carrierSlices/formSlice';
import { useMemo } from 'react';

const Forms = ({ carrier }) => {
  const styles = {
    component: {
      position: 'relative'
    },
    loader: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255,255,255,0.90)'
    },
    filters: {
      display: 'flex',
      marginBottom: 20,
      gap: 10
    },
    filter: {
      width: 200
    },
    action_icon: {
      cursor: 'pointer',
      fontSize: 20,
      marginLeft: 10
    }
  };

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    forms,
    productGroups,
    formTags,
    pagination,
    currentPage,
    searchQuery,
    formTagId,
    productGroupId,
    stateFilter,
    loading,
    exporting,
    error,
    successMessage
  } = useSelector(state => state.forms);

  const [showNewForm, setShowNewForm] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [formToDelete, setFormToDelete] = useState(null);

  useEffect(() => {
    if (params.id) {
      dispatch(setCurrentCarrier(params.id));
      dispatch(fetchFormMetadata(params.id));
      dispatch(
        fetchForms({
          carrier_id: params.id,
          page: currentPage,
          search: searchQuery,
          form_tag_id: formTagId,
          product_group_id: productGroupId,
          state: stateFilter
        })
      );
    }
  }, [params.id, dispatch, currentPage, searchQuery, formTagId, productGroupId, stateFilter]);

  useEffect(() => {
    dispatch(
      fetchForms({
        carrier_id: params.id,
        page: currentPage,
        search: searchQuery,
        form_tag_id: formTagId,
        product_group_id: productGroupId,
        state: stateFilter
      })
    );
  }, [currentPage, searchQuery, formTagId, productGroupId, stateFilter, params.id, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(clearMessages());
    }, 2000);

    return () => clearTimeout(timer);
  }, [error, successMessage, dispatch]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);

    setLocalSearch(value);

    debouncedSetSearchTerm(value);
  };
  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearch));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };
  const handleFilterChange = (name, e) => {
    dispatch(
      setFilters({
        [name]: e.target.value,
        currentPage: 1
      })
    );
  };

  const handlePageChange = page => {
    dispatch(setFilters({ currentPage: page }));
    window.scrollTo(0, 0);
  };

  const openInNewTab = (form, e) => {
    e.stopPropagation();
    window.open(`/#/carriers/${params.id}/forms/${form.id}`);
  };

  const handleSave = () => {
    setShowNewForm(false);
    dispatch(setSuccessMessage('Form successfully added'));
    dispatch(clearCache());
    dispatch(
      fetchForms({
        carrier_id: params.id,
        page: currentPage,
        search: searchQuery,
        form_tag_id: formTagId,
        product_group_id: productGroupId,
        state: stateFilter,
        forceRefresh: true
      })
    );
  };

  const handleToggleStatus = (form, e) => {
    e.stopPropagation();
    dispatch(
      toggleFormStatus({
        formId: form.id,
        isActive: !form.is_active
      })
    );
  };

  const confirmDelete = () => {
    dispatch(deleteForm(formToDelete.id)).then(() => {
      setFormToDelete(null);
    });
  };

  const handleExport = () => {
    const exportParams = {
      carrier_id: params.id,
      page: currentPage,
      search: searchQuery,
      form_tag_id: formTagId,
      product_group_id: productGroupId,
      state: stateFilter
    };

    delete exportParams.page;
    dispatch(exportFormsToCSV(exportParams))
      .unwrap()
      .then(csv => {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.setAttribute('href', url);
        a.setAttribute('download', `${carrier.name} - Forms Report.csv`);
        a.click();
      });
  };

  return (
    <>
      {successMessage && <Alert type='success'>{successMessage}</Alert>}
      {error && <Alert type='error'>{error}</Alert>}

      {formToDelete && (
        <Modal
          buttons={[
            {
              outline: true,
              onClick: () => setFormToDelete(null),
              children: 'Cancel',
              style: { marginLeft: 'auto', marginRight: 10 }
            },
            {
              color: 'RED',
              onClick: confirmDelete,
              children: 'Delete'
            }
          ]}
          onClose={() => setFormToDelete(null)}
          title='Are you sure?'
        >
          <div
            style={{
              padding: 30,
              lineHeight: '30px',
              fontSize: FontSizes.LARGE,
              textAlign: 'center'
            }}
          >
            Be careful...you are about to delete the "{formToDelete.name} ({formToDelete.number})"
            form and all of its versions. This will remove it completely from the platform. Make
            sure you know what you are doing before completing this action.
          </div>
        </Modal>
      )}

      <VersionForm
        form={{ carrier_id: params.id }}
        formTags={formTags}
        onClose={() => setShowNewForm(false)}
        onSave={handleSave}
        productGroups={productGroups}
        show={showNewForm}
      />

      <div style={styles.filters}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by name or number...'
          style={styles.filter}
          value={localSearch}
        />
        <Select
          onChange={e => handleFilterChange('productGroupId', e)}
          options={[{ value: '', label: 'All Product Groups' }].concat(
            productGroups.map(pg => ({ value: pg.id, label: pg.group_name }))
          )}
          style={styles.filter}
          value={productGroupId}
        />
        <Select
          onChange={e => handleFilterChange('stateFilter', e)}
          options={[{ value: '', label: 'All States' }].concat(STATES)}
          style={styles.filter}
          value={stateFilter}
        />
        <Select
          onChange={e => handleFilterChange('formTagId', e)}
          options={[{ value: '', label: 'All Tags' }].concat(
            formTags.map(ft => ({ value: ft.id, label: ft.name }))
          )}
          style={styles.filter}
          value={formTagId}
        />

        <Button disabled={exporting} onClick={handleExport}>
          {exporting ? 'Exporting...' : 'Forms Report'}
        </Button>

        <Button onClick={() => setShowNewForm(true)} style={{ marginLeft: 'auto' }}>
          New Form
        </Button>
      </div>

      {loading && !forms.length ? (
        <Loader />
      ) : (
        <Table
          apiPagination={{
            ...pagination,
            current_page: currentPage,
            onClick: handlePageChange
          }}
          columns={[
            { id: 'id', label: 'ID' },
            { id: 'name', label: 'Name' },
            { id: 'number', label: 'Number' },
            {
              id: 'publish_date',
              render: form =>
                form.publish_date ? formatInTimeZone(form.publish_date, 'UTC', 'MM/dd/yyyy') : null
            },
            {
              id: 'is_active',
              label: 'Status',
              render: form => (
                <Toggle active={form.is_active} onClick={e => handleToggleStatus(form, e)} />
              )
            },
            {
              id: 'actions',
              label: '',
              render: form => (
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <i
                    className='mdi mdi-trash-can-outline'
                    onClick={e => {
                      e.stopPropagation();
                      setFormToDelete(form);
                    }}
                    style={{ ...styles.action_icon, color: Colors.RED.hex }}
                  />
                  <i
                    className='mdi mdi-open-in-new'
                    onClick={e => openInNewTab(form, e)}
                    style={{ ...styles.action_icon, color: Colors.BLUE.hex }}
                  />
                </div>
              ),
              width: 100
            }
          ]}
          data={forms || []}
          onRowClick={form => navigate(`${form.id}`)}
          placeholder='No forms found'
        />
      )}
    </>
  );
};

Forms.propTypes = {
  carrier: PropTypes.object
};

export default Forms;
